import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../services/authContextProvider';
import ObrasAPI from '../services/APIObras';
import { getBackendHost } from '../services/backendHost';

export const NotificationContext = createContext<number>(0);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const authContext = useContext(AuthContext);
  const [notifications, setNotifications] = useState<number>(0);
  const canReceiveNotifications = authContext.canSystem('can_save_any_project')
    || authContext.canSystem('can_validate_any_project');

  useEffect(() => {
    if (authContext.isAuthenticated && authContext.systemCapabilitiesLoaded && canReceiveNotifications) {
      let active = true;
      let sse: EventSource | undefined;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
      let notificationTimer: ReturnType<typeof setTimeout> | undefined;
      let connectionInProgress = false;
      let retryIndex = 0;
      let authenticationBlocked = false;
      let pendingNotification = false;
      const api = ObrasAPI.getInstance();
      const retryDelays = [5000, 10000, 30000, 60000];

      const getErrorStatus = (error: unknown): number | undefined => {
        const candidate = error as { response?: { status?: number }; status?: number };
        return candidate.response?.status || candidate.status;
      };

      const scheduleReconnect = () => {
        if (!active || authenticationBlocked || reconnectTimer) return;
        const delay = retryDelays[Math.min(retryIndex, retryDelays.length - 1)];
        retryIndex = Math.min(retryIndex + 1, retryDelays.length - 1);
        reconnectTimer = setTimeout(() => {
          reconnectTimer = undefined;
          void connect();
        }, delay);
      };

      const publishNotification = () => {
        if (pendingNotification) return;
        pendingNotification = true;
        notificationTimer = setTimeout(() => {
          pendingNotification = false;
          notificationTimer = undefined;
          if (active) setNotifications(prevNotifications => prevNotifications + 1);
        }, 150);
      };

      const connect = async () => {
        if (!active || connectionInProgress || authenticationBlocked) return;
        connectionInProgress = true;
        try {
          const ticket = await api.getNotificationSseTicket();
          if (!active) return;
          sse = new EventSource(
            `${getBackendHost()}/v1/notificaciones/sse?ticket=${encodeURIComponent(ticket)}`,
          );
          sse.onopen = () => {
            retryIndex = 0;
          };
          sse.addEventListener('notification', () => {
            publishNotification();
          }, false);
          sse.onerror = (event) => {
            const status = getErrorStatus(event);
            sse?.close();
            sse = undefined;
            if (status === 401 || status === 403) {
              authenticationBlocked = true;
              return;
            }
            scheduleReconnect();
          };
        } catch (error) {
          const status = getErrorStatus(error);
          if (status === 401 || status === 403) authenticationBlocked = true;
          else scheduleReconnect();
        } finally {
          connectionInProgress = false;
        }
      };

      void connect();
      return () => {
        active = false;
        sse?.close();
        if (reconnectTimer) clearTimeout(reconnectTimer);
        if (notificationTimer) clearTimeout(notificationTimer);
      };
    }
  }, [authContext.email, authContext.isAuthenticated, authContext.systemCapabilitiesLoaded, canReceiveNotifications]);

  return (
    <NotificationContext.Provider value={notifications}>
      {children}
    </NotificationContext.Provider>
  );
}
export default NotificationProvider;
